import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';

type ErrorPanelVariant = 'info' | 'warning' | 'error';

type ErrorPanelProps = {
  message: string;
  variant?: ErrorPanelVariant;
  'data-testid'?: string;
};

type VariantConfig = {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  iconColor: string;
  role: 'alert' | 'status';
  fallbackMessage: string;
};

const VARIANTS: Record<ErrorPanelVariant, VariantConfig> = {
  info: {
    Icon: InformationCircleIcon,
    iconColor: 'text-content-brand',
    role: 'status',
    fallbackMessage: 'No information available.'
  },
  warning: {
    Icon: ExclamationTriangleIcon,
    iconColor: 'text-feedback-warning',
    role: 'status',
    fallbackMessage: 'Something needs your attention.'
  },
  error: {
    Icon: XCircleIcon,
    iconColor: 'text-feedback-error',
    role: 'alert',
    fallbackMessage: 'Something went wrong. Please try again.'
  }
};

export const ErrorPanel = ({
  message,
  variant = 'info',
  'data-testid': testId
}: ErrorPanelProps) => {
  const { Icon, iconColor, role, fallbackMessage } = VARIANTS[variant];

  return (
    <div role={role} className="rounded-2xl bg-white p-4 shadow-plain-lg">
      <div className="flex gap-2" data-testid={testId}>
        <Icon className={`w-16 ${iconColor}`} />
        {message || fallbackMessage}
      </div>
    </div>
  );
};
